// Shared US-map projection + state-label data. Extracted from InteractiveStatesMap
// so both the wizard's state picker and the report's state choropleth can reuse
// the AK/HI/PR repositioning without duplicating the magic numbers. Lives in a
// .ts module (not a component file) so importing it doesn't trip react-refresh.

// Repositioning AK / HI / PR so they fit the lower-48 framing. Mercator
// distorts shapes by latitude, so we relocate Alaska in Mercator-Y space
// before converting back to lat/lng — this is what `transformCoord` does.
const toMercY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 180) / 2));
const fromMercY = (y: number) => (2 * Math.atan(Math.exp(y)) - Math.PI / 2) * 180 / Math.PI;

const AK_SRC_CENTER = { lng: -153, lat: 64 };
const AK_DST_CENTER = { lng: -123, lat: 27 };
const AK_SCALE = 0.35;
const AK_SRC_MERC_Y = toMercY(AK_SRC_CENTER.lat);
const AK_DST_MERC_Y = toMercY(AK_DST_CENTER.lat);
const HI_OFFSET = { lng: 47, lat: 6 };
const PR_OFFSET = { lng: -8, lat: 6 };

export function transformCoord(lng: number, lat: number): [number, number] {
  if (lat > 51 && lng < -129) {
    const newLng = (lng - AK_SRC_CENTER.lng) * AK_SCALE + AK_DST_CENTER.lng;
    const mercY = toMercY(lat);
    const newMercY = (mercY - AK_SRC_MERC_Y) * AK_SCALE + AK_DST_MERC_Y;
    const newLat = fromMercY(newMercY);
    return [newLng, Math.max(-85, Math.min(85, newLat))];
  }
  if (lat < 25 && lng < -154) {
    return [lng + HI_OFFSET.lng, lat + HI_OFFSET.lat];
  }
  if (lat > 17 && lat < 19 && lng > -68 && lng < -64) {
    return [lng + PR_OFFSET.lng, lat + PR_OFFSET.lat];
  }
  return [lng, lat];
}

export function transformGeoJsonCoords(coords: unknown): unknown {
  if (typeof (coords as number[])[0] === 'number') {
    return transformCoord((coords as number[])[0], (coords as number[])[1]);
  }
  return (coords as unknown[]).map((c) => transformGeoJsonCoords(c));
}

export function transformFeature(feature: GeoJSON.Feature): GeoJSON.Feature {
  const geom = feature.geometry;
  if (geom.type === 'Polygon') {
    return { ...feature, geometry: { ...geom, coordinates: transformGeoJsonCoords(geom.coordinates) as GeoJSON.Position[][] } };
  }
  if (geom.type === 'MultiPolygon') {
    return { ...feature, geometry: { ...geom, coordinates: transformGeoJsonCoords(geom.coordinates) as GeoJSON.Position[][][] } };
  }
  return feature;
}

// The US-states GeoJSON keys features by full state name; map to the 2-letter
// codes our data uses.
export const NAME_TO_ABBR: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC',
  Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL',
  Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA',
  Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN',
  Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR',
  Pennsylvania: 'PA', 'Puerto Rico': 'PR', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
};

// Hand-tuned label positions (longitude, latitude) per state, aligned with the
// AK/HI/PR-shifted geometry.
export const STATE_LABEL_RAW: [string, number, number][] = [
  ['AL', -86.8, 32.8], ['AK', -153, 64], ['AZ', -111.7, 34.3], ['AR', -92.4, 34.8],
  ['CA', -119.5, 37.2], ['CO', -105.5, 39.0], ['CT', -72.7, 41.6], ['DE', -75.5, 39.0],
  ['FL', -81.7, 28.7], ['GA', -83.4, 32.7], ['HI', -156.5, 20.8], ['ID', -114.5, 44.4],
  ['IL', -89.2, 40.0], ['IN', -86.3, 39.8], ['IA', -93.5, 42.0], ['KS', -98.3, 38.5],
  ['KY', -85.3, 37.8], ['LA', -91.9, 31.0], ['ME', -69.2, 45.4], ['MD', -76.7, 39.0],
  ['MA', -71.8, 42.3], ['MI', -84.6, 44.3], ['MN', -94.3, 46.3], ['MS', -89.7, 32.7],
  ['MO', -92.5, 38.4], ['MT', -109.6, 47.0], ['NE', -99.8, 41.5], ['NV', -116.6, 39.3],
  ['NH', -71.5, 43.7], ['NJ', -74.4, 40.1], ['NM', -106.0, 34.5], ['NY', -75.5, 42.9],
  ['NC', -79.4, 35.5], ['ND', -100.5, 47.4], ['OH', -82.8, 40.4], ['OK', -97.5, 35.5],
  ['OR', -120.5, 44.0], ['PA', -77.6, 41.0], ['RI', -71.5, 41.7], ['SC', -80.9, 33.9],
  ['SD', -100.2, 44.4], ['TN', -86.3, 35.8], ['TX', -99.0, 31.5], ['UT', -111.7, 39.3],
  ['VT', -72.6, 44.1], ['VA', -79.4, 37.5], ['WA', -120.5, 47.4], ['WV', -80.6, 38.9],
  ['WI', -89.6, 44.6], ['WY', -107.5, 43.0], ['PR', -66.5, 18.2],
];
